import React from 'react';
import { Box, Link, SpaceBetween } from '@amzn/awsui-components-console';
import { LoadingBar, Avatar, ChatBubble } from '@amzn/awsui-chat-components-console';
import MarkdownRenderer from './MarkdownRenderer';

export default function MessageList({ messages, isLoading }) {
    const formatTimestamp = (timestamp) => {
        // Helper function to format date objects consistently
        const formatTime = (date) =>
            date.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
            });

        // Handle different timestamp formats for backwards compatibility
        if (typeof timestamp === 'number') {
            // Raw timestamp number - convert to date
            return formatTime(new Date(timestamp));
        }

        if (typeof timestamp === 'string') {
            // Check if already formatted (contains : and AM/PM)
            if (timestamp.includes(':') && (timestamp.includes('AM') || timestamp.includes('PM'))) {
                return timestamp; // Already formatted, return as-is
            }

            // Try to parse as date string
            const dateObj = new Date(timestamp);
            if (!isNaN(dateObj.getTime())) {
                return formatTime(dateObj);
            }
        }

        // Fallback: use current time
        return formatTime(new Date());
    };

    const renderSources = (sources) => {
        if (!sources || sources.length === 0) return null;

        return (
            <Box margin={{ top: 'xs' }} color="text-body-secondary" fontSize="body-s">
                <strong>Sources:</strong>{' '}
                {sources.map((source, index) => (
                    <Link key={index} href={source} external variant="primary" fontSize="inherit">
                        Document {index + 1}
                    </Link>
                ))}
            </Box>
        );
    };

    const renderMessage = (message) => {
        const isUser = message.type === 'user';
        const isError = message.isError;

        return (
            <div
                key={message.id}
                style={{
                    display: 'flex',
                    justifyContent: isUser ? 'flex-end' : 'flex-start',
                    marginBottom: '16px',
                    alignItems: 'flex-start',
                }}
            >
                {/* Avatar for assistant messages */}
                {!isUser && (
                    <div style={{ marginRight: '12px' }}>
                        <Avatar
                            color="gen-ai"
                            iconName="gen-ai"
                            tooltipText="Device Farm Assistant"
                            ariaLabel="Device Farm Assistant"
                            size="small"
                        />
                    </div>
                )}

                <ChatBubble type={isUser ? 'user' : 'assistant'} variant={isError ? 'error' : 'default'} maxWidth="70%">
                    <SpaceBetween size="xs">
                        <div>
                            {isUser ? (
                                // User messages: render as plain text with pre-wrap
                                <div style={{ whiteSpace: 'pre-wrap' }}>{message.content}</div>
                            ) : (
                                // Assistant messages: render as markdown
                                <MarkdownRenderer content={message.content} />
                            )}
                        </div>

                        {message.sources && renderSources(message.sources)}

                        <Box fontSize="body-xs" color="text-body-secondary" textAlign="right">
                            {formatTimestamp(message.timestamp)}
                        </Box>
                    </SpaceBetween>
                </ChatBubble>

                {/* Avatar for user messages */}
                {isUser && (
                    <div style={{ marginLeft: '12px' }}>
                        <Avatar initials="U" tooltipText="You" ariaLabel="You" size="small" />
                    </div>
                )}
            </div>
        );
    };

    const renderLoadingMessage = () => (
        <div
            style={{
                display: 'flex',
                justifyContent: 'flex-start',
                marginBottom: '16px',
                alignItems: 'flex-start',
            }}
        >
            <div style={{ marginRight: '12px' }}>
                <Avatar
                    color="gen-ai"
                    iconName="gen-ai"
                    tooltipText="Device Farm Assistant"
                    ariaLabel="Device Farm Assistant"
                    size="small"
                />
            </div>
            <ChatBubble type="assistant" variant="default" maxWidth="70%">
                <div style={{ display: 'flex', alignItems: 'center', padding: '8px' }}>
                    <span style={{ marginRight: '8px' }}>Device Farm Assistant is thinking</span>
                    <div style={{ display: 'flex', gap: '3px' }}>
                        {[0, 1, 2].map((i) => (
                            <div
                                key={i}
                                style={{
                                    width: '6px',
                                    height: '6px',
                                    backgroundColor: '#0073bb',
                                    borderRadius: '50%',
                                    animation: `bounce 1.4s infinite ease-in-out ${i * 0.16}s`,
                                }}
                            />
                        ))}
                    </div>
                </div>
            </ChatBubble>
        </div>
    );

    return (
        <div>
            {messages.length === 0 && (
                <Box textAlign="center" color="text-status-inactive">
                    <SpaceBetween size="xs">
                        <p>Hi! I'm your Device Farm assistant.</p>
                        <p>
                            Ask me about device checkout, automation, lab management, or any other Device Farm features.
                        </p>
                    </SpaceBetween>
                </Box>
            )}

            {messages.map(renderMessage)}

            {isLoading && renderLoadingMessage()}

            <style>{`
                @keyframes bounce {
                    0%,
                    80%,
                    100% {
                        transform: translateY(0);
                        opacity: 0.5;
                    }
                    40% {
                        transform: translateY(-8px);
                        opacity: 1;
                    }
                }
            `}</style>
        </div>
    );
}
